<template>
  <HomeWrapperDark class="farming">
    <h3 class="title">{{ $t('homePageFarmingTitle') }}</h3>
    <div class="tokensWrapper">
      <div v-for="token in tokenData" :key="token.key" class="tokenContainer">
        <div class="token">
          <img :src="tokens[token.key].toString()" />
          <el-skeleton :loading="!token.apr" animated>
            <template #template>
              <el-skeleton-item class="chevron-apr-skeleton" />
            </template>
            <template #default>
              <UIChevron :type="ChevronType.Apr">
                <span class="chevron-apr-label">{{ $t('appStatisticAPR') }}:</span>
                <span class="chevron-apr-value">{{ BigNumber(token.apr || 0).decimalPlaces(1) }}%</span>
              </UIChevron>
            </template>
          </el-skeleton>
        </div>
        <div class="distributed-amount">
          {{ BigNumber(token.tokenDistributed || 0).toFormat(0, { groupSeparator: ' ', groupSize: 3 }) }}
        </div>
        <div class="usd-value">
          $ {{ BigNumber(token.usdValue || 0).toFormat(0, { groupSeparator: ' ', groupSize: 3 }) }}
        </div>
      </div>
    </div>
  </HomeWrapperDark>
</template>

<script setup lang="ts">
import { apiUrls } from '~/utils/constants';
import tokens from '~/public/img/tokens';
import { ChevronType } from '~/utils/constants/ui';
import { BigNumber } from 'bignumber.js';
import type { ITokenInfo } from '~/types/apiService';

const { apiUrl } = useEnvs();
const seven_days_ago = new Date();
seven_days_ago.setDate(new Date().getDate() - 7);

const { data } = useFetch<ITokenInfo[]>(apiUrls.statistics.distribution, {
  baseURL: apiUrl,
  query: {
    from: formattedDate(seven_days_ago),
    until: formattedTodayDate()
  }
});

const tokenData = ref<ITokenInfo[]>([]);

watchEffect(async () => {
  if (!data.value || !Array.isArray(data.value)) return;

  const tokensCopy = JSON.parse(JSON.stringify(data.value));

  const aprResponses = await Promise.all(
    tokensCopy.map((token: any) => {
      return $fetch<{ apr: number }>(`${apiUrl}${apiUrls.token.apr}`, {
        params: {
          tokenAddress: token.tokenAddress
        }
      }).catch(() => ({ apr: 0 }));
    })
  );

  tokensCopy.forEach((token: any, i: number) => {
    token.apr = aprResponses[i]?.apr ?? 0;
  });

  tokenData.value = tokensCopy;
});
</script>

<style lang="scss" scoped>
.farming {
  padding: 20px 31px;
  width: fit-content;
  margin-bottom: 35px;

  @media screen and (max-width: 479px) {
    margin-bottom: 0;
    margin-top: 10px;
  }
}

.tokensWrapper {
  display: flex;
  flex-wrap: wrap;
  gap: 60px;
  justify-content: center;
  align-items: center;

  @media screen and (max-width: 479px) {
    justify-content: space-around;
    row-gap: 30px;
  }
}

.title {
  margin-bottom: 48px;
  text-align: center;
}

.tokenContainer {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
}

.token {
  border-radius: 12px;
  border: 1px solid #1e353e;
  background-image: linear-gradient(
    103.98deg,
    rgba(0, 0, 0, 0) 23.92%,
    rgba(0, 0, 0, 0.36) 63.2%,
    rgba(0, 0, 0, 0) 97.87%
  );
  box-shadow: 2px 4px 5px 0px rgba(0, 0, 0, 0.25);
  width: 100px;
  height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  padding-top: 10px;

  img {
    width: 70px;
  }

  @media screen and (max-width: 479px) {
    width: 76px;
    height: 78px;

    img {
      width: unset;
      height: 100%;
      object-fit: contain;
    }
  }
}

.chevron {
  position: absolute;
  top: -12px;
  right: -10px;

  @media screen and (max-width: 479px) {
    right: unset;
    left: 15%;
  }
}

.distributed-amount {
  font-family: Eczar;
  font-weight: 700;
  font-size: 20px;
  line-height: 130%;
  letter-spacing: 0%;
  color: #88e1ff;
}

.usd-value {
  font-family: Eczar;
  font-weight: 500;
  font-size: 20px;
  line-height: 130%;
  letter-spacing: 0%;
  color: #fff;
}
</style>
